import { tick } from 'svelte';

/**
 * Check if an element is fully visible within its container
 * @param element - The element to check
 * @param container - The container element
 * @returns boolean indicating if the element is fully visible
 */
export function isElementFullyVisible(element: Element, container: Element): boolean {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return elementRect.left >= containerRect.left && elementRect.right <= containerRect.right;
}

/**
 * Scroll an element into view if it's not already fully visible
 * @param element - The element to scroll into view
 * @param container - The container element
 * @param behavior - Scroll behavior (smooth, auto)
 * @returns Whether the element was scrolled or not
 */
export function scrollElementIntoViewIfNeeded(
    element: Element,
    container: Element,
    behavior: ScrollBehavior = 'smooth'
): void {
    if (!isElementFullyVisible(element, container)) {
        element.scrollIntoView({
            behavior,
            block: 'nearest',
            inline: 'nearest'
        });
    }
}

interface ScrollSelectedOptions {
    selector: string;
    isLoaded?: boolean;
    delay?: number;
    behavior?: ScrollBehavior;
}

export function scrollSelectedIntoView(
    node: HTMLElement,
    options: ScrollSelectedOptions
): { update: (newOptions?: Partial<ScrollSelectedOptions>) => void; destroy: () => void } {
    const { selector, behavior = 'smooth' } = options;

    async function scrollActiveElementIntoView() {
        // Wait for any pending DOM updates to complete
        await tick();

        const selectedElement = node.querySelector(selector);
        if (selectedElement) {
            scrollElementIntoViewIfNeeded(selectedElement, node, behavior);
        }
    }

    if (node && options.isLoaded) {
        scrollActiveElementIntoView();
    }

    return {
        update(newOptions: Partial<ScrollSelectedOptions> = {}) {
            options = { ...options, ...newOptions };

            if (node && options.isLoaded) {
                scrollActiveElementIntoView();
            }
        },

        destroy() {}
    };
}
